import ResumePreview from "./ResumePreview";
import { useAuth } from "../contexts/useAuth";
import { useResume } from "../hooks/useResume";
import React, { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../contexts/TranslationContext";
import { Modal, PromptModal } from "../components/Modal";
import { useForm } from "react-hook-form";
import { minLengthValidation } from "../utils/validation.js";
import { Loader2 } from "lucide-react";

function detectLanguage(text) {
  const ptWords = /\b(de|da|do|para|com|que|uma|seu|sua|experiência|desenvolvimento|requisitos|responsabilidades|vaga|empresa)\b/gi;
  const matches = text.match(ptWords);
  return matches && matches.length > 3 ? "pt" : "en";
}

export default function GenerateResumeWithAI() {
  const { token, fetchCredits } = useAuth();
  const { resume } = useResume(token);
  const [loading, setLoading] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [descricoesGeradas, setDescricoesGeradas] = useState(null);
  const [resumeAntigo, setResumeAntigo] = useState(null);
  const navigate = useNavigate();
  const [showNameModal, setShowNameModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [outputLanguage, setOutputLanguage] = useState("auto");
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const vagaDescricao = watch("vagaDescricao", "");

  const formatDescricao = (descricao) =>
    descricao
      .split(/(?<=\.\s)/)
      .filter(Boolean)
      .map((sent, idx) => (
        <p key={idx} className="text-sm text-gray-400 ml-4 mb-2">{sent.trim()}</p>
      ));

  const handleGenerateClick = (_data) => {
    if (!resume?.experiences?.length) {
      toast.error(t("generateResume.errorMissingFields"));
      return;
    }
    setShowCreditModal(true);
  };

  const handleGenerate = async () => {
    setShowCreditModal(false);
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/generate-resume-description`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          experiences: resume.experiences,
          vagaDescricao,
          summary: resume.summary,
          desiredRole: resume.desiredRole,
          skills: resume.skills,
          languages: resume.languages,
          outputLanguage,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Cobra o crédito SOMENTE após a geração ter sucesso (evita cobrar sem entregar)
        const creditRes = await fetch(`${import.meta.env.VITE_API_URL}/api/credits/consume`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ amount: 1 }),
        });

        if (!creditRes.ok) {
          const creditData = await creditRes.json();
          toast.error(creditData.error || t("toasts.creditsInsufficient"));
          setLoading(false);
          return;
        }

        fetchCredits(token);

        setDescricoesGeradas(data.descricoesGeradas);
        setResumeAntigo(resume);
        setPreviewReady(true);
      } else {
        toast.error(data.error || t("generateResume.errorGenerate"));
      }
    } catch (err) {
      toast.error(t("generateResume.errorConnection"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCurriculo = async (nomeCurriculo) => {
    setShowNameModal(false);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/resumes/alternativos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nomeCurriculo,
          name: resumeAntigo.name,
          phone: resumeAntigo.phone,
          email: resumeAntigo.email,
          address: resumeAntigo.address,
          website: resumeAntigo.website,
          linkedin: resumeAntigo.linkedin,
          github: resumeAntigo.github,
          desiredRole: descricoesGeradas.desiredRole || resumeAntigo.desiredRole,
          summary: descricoesGeradas.summary || resumeAntigo.summary,
          skills: descricoesGeradas.skills || resumeAntigo.skills,
          languages: descricoesGeradas.languages || resumeAntigo.languages,
          references: resumeAntigo.references,
          experiences: descricoesGeradas.experiences,
          education: resumeAntigo.education,
          cvLanguage: outputLanguage === "auto" ? (detectLanguage(vagaDescricao)) : outputLanguage
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        toast.success(t("generateResume.successSave"));
        navigate("/configure-resume");
      } else {
        toast.error(t("generateResume.errorSave"));
      }
    } catch (error) {
      toast.error(t("generateResume.errorConnection"));
    }
  };

  return (
    <div className="flex-1 bg-[#0a0a0a] text-white p-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-6">{t("generateResume.title")}</h1>

      <form onSubmit={handleSubmit(handleGenerateClick)} noValidate>
        <div className="mb-4">
          <textarea
            placeholder={t("generateResume.textareaPlaceholder")}
            className={`w-full h-40 p-3 rounded bg-white/5 border text-white placeholder-gray-500 focus:border-brand-400 focus:outline-none transition ${
              errors.vagaDescricao ? 'border-red-500' : 'border-white/10'
            }`}
            {...register("vagaDescricao", {
              required: t("generateResume.errorMissingFields"),
              ...minLengthValidation(t, 20, t("generateResume.textareaPlaceholder"))
            })}
          ></textarea>
          {errors.vagaDescricao && (
            <p className="text-red-500 text-sm mt-1">{errors.vagaDescricao.message}</p>
          )}
        </div>

      {/* Language Toggle */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-sm text-gray-400">{t("generateResume.outputLanguage")}:</span>
        <div className="flex rounded-lg overflow-hidden border border-white/10">
          {[
            { value: "auto", label: "Auto" },
            { value: "pt", label: "PT-BR" },
            { value: "en", label: "English" },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setOutputLanguage(opt.value)}
              className={`px-4 py-1.5 text-sm font-medium transition ${
                outputLanguage === opt.value
                  ? "bg-brand-500 text-black"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

        <div className="flex justify-between mt-4 mb-8">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded hover:scale-105 transition"
          >
            {t("generateResume.back")}
          </button>

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-brand-500 hover:bg-brand-400 text-black font-semibold rounded hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="animate-spin" size={20} />}
            {loading ? t("generateResume.loading") : t("generateResume.sendToAI")}
          </button>
        </div>
      </form>

      {previewReady && descricoesGeradas && resumeAntigo && (
        <>
          <h2 className="text-2xl font-bold text-brand-400 mb-6">
            {t("generateResume.comparisonTitle")}
          </h2>

          <div className="flex flex-col md:flex-row gap-6 items-stretch">
            <div className="bg-white/10 p-6 rounded-lg shadow-lg w-full md:w-1/2 flex flex-col flex-1">
              <h2 className="text-xl font-bold text-brand-400 mb-4">{t("generateResume.oldResume")}</h2>
              <div className="space-y-4">
                {resumeAntigo.experiences.map((exp, idx) => (
                  <div key={idx} className="space-y-2">
                    <p className="font-semibold">{exp.role} @ {exp.company}</p>
                    {formatDescricao(exp.descricao)}
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full md:w-1/2 flex flex-col flex-1">
              <ResumePreview descricoesGeradas={descricoesGeradas} resumeAntigo={resumeAntigo} />
              <button
                onClick={() => setShowNameModal(true)}
                className="mt-6 px-4 py-2 bg-green-600 text-white rounded hover:scale-105 transition"
              >
                {t("generateResume.saveAsNew")}
              </button>
            </div>
          </div>
        </>
      )}
      <Modal
        isOpen={showCreditModal}
        onClose={() => { setShowCreditModal(false); toast.info(t("generateResume.operationCancelled")); }}
        onConfirm={handleGenerate}
        message={t("generateResume.creditConfirmMessage")}
        confirmText={t("generateResume.creditConfirm")}
        cancelText={t("generateResume.creditCancel")}
      />

      <PromptModal
        isOpen={showNameModal}
        onClose={() => setShowNameModal(false)}
        onConfirm={handleSaveCurriculo}
        message={t("generateResume.namePrompt")}
        confirmText={t("configureResume.save")}
        cancelText={t("configureResume.cancel")}
        placeholder={t("generateResume.namePrompt")}
      />
      </div>
    </div>
  );
}
